/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../components/layout";
import fetchHelper from "../../services/fetchHelper";
import { CPTCheckbox } from "./CPTCheckbox";
import groupBy from "lodash/groupBy";
import cn from "classnames";
import useFeatureFlag from "../../utils/featureFlag";
import { CPT_CODES_ENABLED } from "../../constants/AppConfigConstants";
const NEW_CPT_CODES_ENABLED = "new-cpt-codes-enabled";
const NEW_CPT_CODES_ENABLED_FOR_PRACTICES = "new-cpt-codes-practices-enabled";

export const CPTCodes = ({ suggestions: suggestionsProp, setSuggestions, setShowCPT, showCPT }) => {
  const { headerDetails: userInfo } = useContext(LayoutContext);
  const [cptCodes, setCptCodes] = useState(null);
  const { getFlag } = useFeatureFlag();

  useEffect(() => {
    fetchCPTCodes();
  }, []);

  const showSuggestButton = () => {
    const newCptCodes = getFlag(CPT_CODES_ENABLED);
    if (newCptCodes.enabled && newCptCodes.attributes[NEW_CPT_CODES_ENABLED]) {
      const allPracticesForNewCptCodes = newCptCodes.attributes[NEW_CPT_CODES_ENABLED_FOR_PRACTICES] || [];
      return (
        allPracticesForNewCptCodes.length === 0 || allPracticesForNewCptCodes.includes(userInfo.location.id)
      );
    }
    return false;
  };

  const fetchCPTCodes = async () => {
    try {
      const allCTPCodes = await fetchHelper.get({
        url: `/patientmanagement/rest/v2/interrogations/${userInfo.id}/cpt_code`,
      });
      const groupedCPTCodes = groupBy(allCTPCodes, (item) => item.cptCodeGroupName);
      const codesToDisplay = Object.keys(groupedCPTCodes).map((group) => {
        const customCodes = groupedCPTCodes[group].map((singleCode) => ({
          interrogationId: singleCode.interrogationId,
          codeId: singleCode.cptCodeId,
          codeClinicId: singleCode.cptCodeClinicId,
          codeName: singleCode.cptCodeName,
          selected: singleCode.selected,
        }));

        return {
          name: group,
          apiName: "cpt_code",
          esignStatements: customCodes,
        };
      });
      setCptCodes(codesToDisplay);
    } catch {
      setCptCodes(null);
    }
  };

  const showSuggestions = () => {
    if (suggestionsProp.length > 0) {
      setSuggestions([]);
    } else {
      const {
        device: { deviceType },
        nintyDayBillStatus,
        thirtyDayBillStatus,
      } = userInfo;
      const suggestions = [];
      let dt = deviceType.toLowerCase();

      if ((dt === "pacemaker" || dt === "crtp") && nintyDayBillStatus === "BILLABLE") {
        suggestions.push("93294");
      }

      if ((dt === "icd" || dt === "crtd") && nintyDayBillStatus === "BILLABLE") {
        suggestions.push("93295");
      }

      if (
        dt !== "ilr" &&
        dt !== "other" &&
        nintyDayBillStatus === "BILLABLE" &&
        thirtyDayBillStatus !== "BILLABLE"
      ) {
        suggestions.push("93296");
      }

      if (dt !== "ilr" && dt !== "other" && thirtyDayBillStatus === "BILLABLE") {
        suggestions.push("93297");
      }

      if (dt === "ilr" && thirtyDayBillStatus === "BILLABLE") {
        suggestions.push("93298");
      }

      if (dt !== "other" && thirtyDayBillStatus === "BILLABLE") {
        suggestions.push("93299/G2066");
      }

      setSuggestions(suggestions);
    }
  };

  return (
    <fieldset id="cpt-codes-section">
      <div className="grp-hdr">
        <button
          className="btn btn-sm btn-hide-section"
          onClick={() => setShowCPT((prev) => !prev)}
          type="button"
        >
          <i className={cn("ti", { "ti-eye-slash": showCPT }, { "ti-eye": !showCPT })} />
          <span>{showCPT ? "Hide" : "Show"}</span>
        </button>
        <h3>CPT Codes</h3>
        {showCPT && !showSuggestButton() ? (
          <button className="btn btn-sm btn-teal-l btn-suggest" onClick={showSuggestions} type="button">
            Suggest
          </button>
        ) : null}
      </div>
      {cptCodes &&
        cptCodes.map((cpt) => (
          <CPTCheckbox
            key={`cpt-${cpt.name}`}
            data={cpt}
            globalShow={showCPT}
            suggestions={suggestionsProp}
          />
        ))}
    </fieldset>
  );
};
